// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as Core from './configuration.core';
import * as ModelExport from './configuration.modelExport';

export function isExportModel(source: any): source is IExport {
    // Method Implementation: simple
    return source.modelName === 'Export';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export function isInvalidFieldModel(source: any): source is IInvalidField {
    // Method Implementation: simple
    return source.modelName === 'InvalidField';
}

export interface ExportZodSchemaShape {
    fields?: FieldZodSchemaShape[];

    invalidFields?: InvalidFieldZodSchemaShape[];
}

export interface FieldZodSchemaShape {
    name?: string;

    value?: string;
}

export interface InvalidFieldZodSchemaShape {
    name?: string;

    errorMessage?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const ExportZodSchema: Zod.ZodSchema<ExportZodSchemaShape> = Zod.lazy(() => Zod.object({
    fields: Zod.array(FieldZodSchema).optional(),
    invalidFields: Zod.array(InvalidFieldZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const InvalidFieldZodSchema: Zod.ZodSchema<InvalidFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    errorMessage: Zod.string().optional()
}).nonstrict());

export interface IExport extends Wellspring.IModel {
    readonly modelName: string;

    readonly fields: Field[];

    readonly invalidFields: InvalidField[];

    readonly isFieldsDefined: boolean;

    readonly isInvalidFieldsDefined: boolean;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;
}

export interface IInvalidField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly errorMessage: string;

    readonly isNameDefined: boolean;

    readonly isErrorMessageDefined: boolean;
}

export class Export implements IExport {
    public static readonly modelName: string = 'Export';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'fields', 'invalidFields' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Export.modelName;
    }

    private _fields: Field[];

    private _invalidFields: InvalidField[];

    public get fields(): Field[] {
        // Method Implementation: simple
        return this._fields;
    }

    public get invalidFields(): InvalidField[] {
        // Method Implementation: simple
        return this._invalidFields;
    }

    public set fields(value: Field[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    public set invalidFields(value: InvalidField[]) {
        // Method Implementation: simple
        this._invalidFields = value;
        this._isInvalidFieldsDefined = true;
    }

    private _isFieldsDefined: boolean;

    private _isInvalidFieldsDefined: boolean;

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public get isInvalidFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isInvalidFieldsDefined;
    }

    public constructor();
    public constructor(source: IExport);
    public constructor(source: Partial<Pick<Export, 'fields' | 'invalidFields'>>);
    public constructor(source?: IExport | Partial<Pick<Export, 'fields' | 'invalidFields'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isExportModel(source)) {
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.isInvalidFieldsDefined) {
                    this._invalidFields = source.invalidFields;
                    this._isInvalidFieldsDefined = true;
                }
                else {
                    this._invalidFields = [];
                    this._isInvalidFieldsDefined = true;
                }
            }
            else {
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.invalidFields !== undefined) {
                    this._invalidFields = source.invalidFields;
                    this._isInvalidFieldsDefined = true;
                }
                else {
                    this._invalidFields = [];
                    this._isInvalidFieldsDefined = true;
                }
            }
        }
        else {
            this._fields = [];
            this._isFieldsDefined = true;
        
            this._invalidFields = [];
            this._isInvalidFieldsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Export.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        if (this.isInvalidFieldsDefined && this.invalidFields !== undefined)
            definedProperties.push('invalidFields');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'fields':
                return this.fields;
            case 'invalidFields':
                return this.invalidFields;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
        
        if (this.isInvalidFieldsDefined && this.invalidFields !== undefined) {
            if (this.invalidFields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('invalidFields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.invalidFields.length));
                for (let i = 0; i < this.invalidFields.length; i++) {
                    const item = this.invalidFields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('invalidFields', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: Field = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Field.modelName) {
                                item = new Field();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Field.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            case 'invalidFields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.invalidFields = [];
                
                    let item: InvalidField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === InvalidField.modelName) {
                                item = new InvalidField();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${InvalidField.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'invalidFields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'invalidFields'`);
                        }
                
                        this.invalidFields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'invalidFields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'invalidFields'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // invalidFields
        if (this.isInvalidFieldsDefined && this.invalidFields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.invalidFields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.invalidFields.length);
            
                for (const item of this.invalidFields) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Field;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Field();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // invalidFields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: InvalidField;
            
                    this.invalidFields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new InvalidField();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.invalidFields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Export {
        // Method Implementation: simple
        const schema = ExportZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ExportZodSchemaShape): Export {
        // Method Implementation: parseZodSchema
        return new Export({
            fields: schema.fields?.map(i => Field.parseZodSchema(i)),
            invalidFields: schema.invalidFields?.map(i => InvalidField.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
            invalidFields: this.isInvalidFieldsDefined ? (this.invalidFields?.map(item => item)) : undefined,
        };
    }

    public clone(): Export {
        // Method Implementation: clone
        return new Export({
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined,
            invalidFields: this.isInvalidFieldsDefined ? this.invalidFields?.map(i => i.clone()) : undefined
        });
    }
}

export class Field implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Field.modelName;
    }

    private _name: string;

    private _value: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<Field, 'name' | 'value'>>);
    public constructor(source?: IField | Partial<Pick<Field, 'name' | 'value'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Field.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Field {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): Field {
        // Method Implementation: parseZodSchema
        return new Field({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): Field {
        // Method Implementation: clone
        return new Field({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class InvalidField implements IInvalidField {
    public static readonly modelName: string = 'InvalidField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'errorMessage' ];

    public get modelName(): string {
        // Method Implementation: simple
        return InvalidField.modelName;
    }

    private _name: string;

    private _errorMessage: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get errorMessage(): string {
        // Method Implementation: simple
        return this._errorMessage;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set errorMessage(value: string) {
        // Method Implementation: simple
        this._errorMessage = value;
        this._isErrorMessageDefined = true;
    }

    private _isNameDefined: boolean;

    private _isErrorMessageDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isErrorMessageDefined(): boolean {
        // Method Implementation: simple
        return this._isErrorMessageDefined;
    }

    public constructor();
    public constructor(source: IInvalidField);
    public constructor(source: Partial<Pick<InvalidField, 'name' | 'errorMessage'>>);
    public constructor(source?: IInvalidField | Partial<Pick<InvalidField, 'name' | 'errorMessage'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isInvalidFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isErrorMessageDefined) {
                    this._errorMessage = source.errorMessage;
                    this._isErrorMessageDefined = true;
                }
                else {
                    this._errorMessage = '';
                    this._isErrorMessageDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.errorMessage !== undefined) {
                    this._errorMessage = source.errorMessage;
                    this._isErrorMessageDefined = true;
                }
                else {
                    this._errorMessage = '';
                    this._isErrorMessageDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._errorMessage = '';
            this._isErrorMessageDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return InvalidField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isErrorMessageDefined && this.errorMessage !== undefined)
            definedProperties.push('errorMessage');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'errorMessage':
                return this.errorMessage;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isErrorMessageDefined && this.errorMessage !== undefined) {
            if (this.errorMessage !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('errorMessage', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.errorMessage);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('errorMessage', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'errorMessage':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.errorMessage = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'errorMessage' cannot be null`);
                    this.errorMessage = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'errorMessage'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // errorMessage
        if (this.isErrorMessageDefined && this.errorMessage !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.errorMessage === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.errorMessage);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // errorMessage
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.errorMessage = <any>null;
                    break;
                case 1:
                    this.errorMessage = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): InvalidField {
        // Method Implementation: simple
        const schema = InvalidFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: InvalidFieldZodSchemaShape): InvalidField {
        // Method Implementation: parseZodSchema
        return new InvalidField({
            name: schema.name,
            errorMessage: schema.errorMessage
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            errorMessage: this.isErrorMessageDefined ? ((this.errorMessage === null ? undefined : this.errorMessage)) : undefined,
        };
    }

    public clone(): InvalidField {
        // Method Implementation: clone
        return new InvalidField({
            name: this.isNameDefined ? this.name : undefined,
            errorMessage: this.isErrorMessageDefined ? this.errorMessage : undefined
        });
    }
}

export interface IProviderService {
    createExport(entity: Core.Entity, exportModel: ModelExport.Export, childQualifier: string): Promise<Export>;
}

export class ProviderServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public createExport(entity: Core.Entity, exportModel: ModelExport.Export, childQualifier?: string): Promise<Export>;
    public createExport(actionContext: Wellspring.ActionContext, entity: Core.Entity, exportModel: ModelExport.Export, childQualifier?: string): Promise<Export>;
    public async createExport(...args: unknown[]): Promise<Export> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.union([
            Zod.tuple([
                // {"template":"model","className":"Core.Entity"}
                Zod.instanceof(Core.Entity), 
                // {"template":"model","className":"ModelExport.Export"}
                Zod.instanceof(ModelExport.Export)
            ]), 
            Zod.tuple([
                // {"template":"model","className":"Core.Entity"}
                Zod.instanceof(Core.Entity), 
                // {"template":"model","className":"ModelExport.Export"}
                Zod.instanceof(ModelExport.Export), 
                // {"template":"string"}
                Zod.string().optional()
            ])
        ]).check(args)) {
            const [ entity, exportModel, childQualifier ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createExport');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createExport', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("c3ea4b9d1dd48fc4fef3e8881e54c70fc9fc39cb74357519e9987015ab626460");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (exportModel && exportModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (exportModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            exportModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (childQualifier && childQualifier !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (childQualifier === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(childQualifier);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Export>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createExport`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                exportModel: JSON.parse(JSON.stringify(exportModel)),
                                childQualifier: JSON.parse(JSON.stringify(childQualifier))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createExport" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Export;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Export();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createExport`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        exportModel: JSON.parse(JSON.stringify(exportModel)),
                        childQualifier: JSON.parse(JSON.stringify(childQualifier))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createExport`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        exportModel: JSON.parse(JSON.stringify(exportModel)),
                        childQualifier: JSON.parse(JSON.stringify(childQualifier))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Export':
                return new Export();
            case 'Field':
                return new Field();
            case 'InvalidField':
                return new InvalidField();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Export':
                return new Export(source);
            case 'Field':
                return new Field(source);
            case 'InvalidField':
                return new InvalidField(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Export':
                return Export.parse(source);
            case 'Field':
                return Field.parse(source);
            case 'InvalidField':
                return InvalidField.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
